/**
 * ORA - AI Leadership Agent
 * Quick Observation Service
 * Handles submission of quick observations to Supabase and email notifications
 * Supports anonymous submissions - user_id and user_email are optional
 */

import { supabase, isSupabaseAvailable } from './supabase';

export interface QuickObservationData {
  observation: string;
  additionalContext?: string;
  topic?: string;
  userEmail?: string;
  userId?: string; // Optional - supports anonymous submissions
}

/**
 * Submit a quick observation to Supabase
 * Also triggers an email notification to cara@oratf.info
 */
export async function submitQuickObservation(data: QuickObservationData): Promise<{
  success: boolean;
  error?: string;
  id?: string;
}> {
  try {
    // Check if Supabase is available - silently skip if not configured (dev mode) - silently skip if not configured (dev mode)
    if (!isSupabaseAvailable() || !supabase) {
      // Silently skip in devel// Silently skip in developmepment mt modde  whehen  Supabase is is not configurfigured
      return {
        success: false,
        error: 'Backend service not configured. Your observation was not saved.'
      };
    }

    // Insert the observation into the database
    const { data: insertedData, error: insertError } = await supabase
      .from('quick_observations')
      .insert({
        observation: data.observation,
        additional_context: data.additionalContext || null,
        topic: data.topic || 'AI Bias or Risk',
        user_email: data.userEmail || null,
        user_id: data.userId || null,
      })
      .select()
      .single();

    if (insertError) {
      console.error('Error inserting quick observation:', insertError);
      return {
        success: false,
        error: 'Failed to save observation. Please try again.'
      };
    }

    console.log('✅ Quick Observation saved:', insertedData.id);

    // Send email notification via Supabase Edge Function
    try {
      const { error: emailError } = await supabase.functions.invoke('send-observation-email', {
        body: {
          observation: data.observation,
          additionalContext: data.additionalContext || '',
          topic: data.topic || 'AI Bias or Risk',
          userEmail: data.userEmail || 'Anonymous',
          userId: data.userId || 'N/A',
          observationId: insertedData.id,
          recipientEmail: 'cara@oratf.info'
        }
      });

      if (emailError) {
        console.error('Error sending email notification:', emailError);
        // Don't fail the whole operation if email fails
        console.warn('Observation saved but email notification failed');
      } else {
        console.log('✅ Email notification sent to cara@oratf.info');
      }
    } catch (emailError) {
      console.error('Email notification error:', emailError);
      // Don't fail the whole operation if email fails
    }

    return {
      success: true,
      id: insertedData.id
    };

  } catch (error) {
    console.error('Unexpected error submitting quick observation:', error);
    return {
      success: false,
      error: 'An unexpected error occurred. Please try again.'
    };
  }
}